import React, { useState, useEffect, useCallback } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import Modal from '../../components/Layout/Modal';
import sessionManagementService from '../../services/sessionManagementService';

/**
 * 세션 관리 페이지
 * 
 * 통합 세션 관리 기능을 제공합니다.
 * - 사용자 세션 모니터링
 * - 의료진 우회 세션 모니터링
 * - 세션 통계
 * - 세션 정책 관리
 */
const SessionManagementPage = () => {
  // 상태 관리
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // all, user, medical_bypass, policies
  const [statistics, setStatistics] = useState(null);
  
  // 사용자 세션 상태
  const [userSessions, setUserSessions] = useState([]);
  const [userPagination, setUserPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // 의료진 우회 세션 상태
  const [medicalSessions, setMedicalSessions] = useState([]);
  const [medicalPagination, setMedicalPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // 필터 상태
  const [filters, setFilters] = useState({
    status: 'active',
    user_id: '',
    ip_address: ''
  });
  
  // 선택된 세션
  const [selectedSessions, setSelectedSessions] = useState([]);
  
  // 모달 상태
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policies, setPolicies] = useState({});

  // 데이터 로드
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // 통계 조회
      const statsRes = await sessionManagementService.getStatistics();
      if (statsRes.success) {
        setStatistics(statsRes.data);
      }
      
      // 세션 목록 조회
      const sessionRes = await sessionManagementService.getSessionList({
        ...filters,
        page: userPagination.page,
        limit: userPagination.limit
      });
      
      if (sessionRes.success) {
        const data = sessionRes.data;
        if (data.user_sessions) {
          setUserSessions(data.user_sessions.sessions || []);
          setUserPagination(prev => ({
            ...prev,
            total: data.user_sessions.total || 0,
            total_pages: data.user_sessions.total_pages || 0
          }));
        }
        if (data.medical_bypass_sessions) {
          setMedicalSessions(data.medical_bypass_sessions.sessions || []);
          setMedicalPagination(prev => ({
            ...prev,
            total: data.medical_bypass_sessions.total || 0,
            total_pages: data.medical_bypass_sessions.total_pages || 0
          }));
        }
      }
    } catch (error) {
      console.error('데이터 로드 실패:', error);
      alert('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filters, userPagination.page, userPagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 정책 로드
  const fetchPolicies = async () => {
    try {
      const res = await sessionManagementService.getPolicies();
      if (res.success) {
        setPolicies(res.data);
      }
    } catch (error) {
      console.error('정책 로드 실패:', error);
    }
  };

  // 필터 변경
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setUserPagination(prev => ({ ...prev, page: 1 }));
  };

  // 필터 초기화
  const handleResetFilters = () => {
    setFilters({ status: 'active', user_id: '', ip_address: '' });
    setUserPagination(prev => ({ ...prev, page: 1 }));
  };

  // 세션 상세 보기
  const handleViewDetail = async (session, type) => {
    try {
      const res = await sessionManagementService.getSessionDetail(session.session_id, type);
      if (res.success) {
        setSelectedSession({ ...res.data, session_type: type });
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('세션 상세 조회 실패:', error);
      alert('세션 정보를 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 세션 종료 확인
  const handleTerminateConfirm = (session, type) => {
    setSelectedSession({ ...session, session_type: type });
    setTerminateReason('');
    setShowTerminateModal(true);
  };

  // 세션 종료 실행
  const handleTerminate = async () => {
    if (!selectedSession) return;
    
    try {
      setLoading(true);
      const res = await sessionManagementService.terminateSession(
        selectedSession.session_id,
        selectedSession.session_type,
        terminateReason || '관리자에 의한 강제 종료'
      );
      
      if (res.success) {
        alert('세션이 종료되었습니다.');
        setShowTerminateModal(false);
        fetchData();
      } else {
        alert(res.message || '세션 종료에 실패했습니다.');
      }
    } catch (error) {
      console.error('세션 종료 실패:', error);
      alert('세션 종료 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 정책 저장
  const handleSavePolicies = async () => {
    try {
      setLoading(true);
      const res = await sessionManagementService.updatePolicies(policies);
      if (res.success) {
        alert('정책이 저장되었습니다.');
        setShowPolicyModal(false);
      } else {
        alert(res.message || '정책 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('정책 저장 실패:', error);
      alert('정책 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 날짜 포맷
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR');
  };

  // User-Agent 파싱
  const parseUserAgent = (ua) => {
    if (!ua) return { browser: '-', os: '-' };
    
    let browser = '-';
    let os = '-';
    
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';
    
    return { browser, os };
  };

  // 세션 상태 뱃지
  const getStatusBadge = (session) => {
    const now = new Date();
    const expires = new Date(session.expires_at);
    
    if (session.terminated_at) {
      return <span className="badge bg-secondary">종료됨</span>;
    }
    if (expires < now) {
      return <span className="badge bg-warning">만료됨</span>;
    }
    return <span className="badge bg-success">활성</span>;
  };

  // 통계 카드 렌더링
  const renderStatisticsCards = () => {
    if (!statistics) return null;
    
    const userStats = statistics.user_sessions || {};
    const medicalStats = statistics.medical_bypass || {};
    
    return (
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title text-primary">
                {userStats.summary?.total_active || 0}
              </h5>
              <p className="card-text">활성 세션</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title text-success">
                {userStats.today?.new_sessions || 0}
              </h5>
              <p className="card-text">오늘 로그인</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title text-info">
                {medicalStats.active_count || 0}
              </h5>
              <p className="card-text">의료진 우회 세션</p>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center">
            <div className="card-body">
              <h5 className="card-title text-danger">
                {userStats.today?.forced_terminations || 0}
              </h5>
              <p className="card-text">강제 종료</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 사용자 세션 테이블 렌더링
  const renderUserSessionsTable = () => (
    <div className="table_area">
      <div className="table">
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>사용자 ID</th>
              <th>이름</th>
              <th>역할</th>
              <th>IP 주소</th>
              <th>브라우저</th>
              <th>생성 시간</th>
              <th>마지막 활동</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {userSessions.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                  세션이 없습니다.
                </td>
              </tr>
            ) : (
              userSessions.map((session, idx) => {
                const { browser, os } = parseUserAgent(session.user_agent);
                return (
                  <tr key={session.session_id}>
                    <td>{(userPagination.page - 1) * userPagination.limit + idx + 1}</td>
                    <td>{session.user_id}</td>
                    <td>{session.user_name}</td>
                    <td>{session.user_role}</td>
                    <td>{session.ip_address || '-'}</td>
                    <td>{browser} / {os}</td>
                    <td>{formatDate(session.created_at)}</td>
                    <td>{formatDate(session.last_active_at)}</td>
                    <td>{getStatusBadge(session)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="button"
                          className="btn fourbtn"
                          value="상세"
                          onClick={() => handleViewDetail(session, 'user')}
                        />
                        {!session.terminated_at && (
                          <input
                            type="button"
                            className="btn fourbtn btn-danger"
                            value="종료"
                            onClick={() => handleTerminateConfirm(session, 'user')}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // 의료진 우회 세션 테이블 렌더링
  const renderMedicalSessionsTable = () => (
    <div className="table_area">
      <div className="table">
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>사용자 ID</th>
              <th>이름</th>
              <th>의료진 ID</th>
              <th>의료진명</th>
              <th>우회 레벨</th>
              <th>생성 시간</th>
              <th>만료 시간</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {medicalSessions.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                  의료진 우회 세션이 없습니다.
                </td>
              </tr>
            ) : (
              medicalSessions.map((session, idx) => (
                <tr key={session.session_id}>
                  <td>{(medicalPagination.page - 1) * medicalPagination.limit + idx + 1}</td>
                  <td>{session.user_id}</td>
                  <td>{session.user_name}</td>
                  <td>{session.staff_id}</td>
                  <td>{session.staff_name || '-'}</td>
                  <td>
                    <span className={`badge ${session.bypass_level >= 3 ? 'bg-danger' : session.bypass_level >= 2 ? 'bg-warning' : 'bg-info'}`}>
                      레벨 {session.bypass_level}
                    </span>
                  </td>
                  <td>{formatDate(session.created_at)}</td>
                  <td>{formatDate(session.expires_at)}</td>
                  <td>
                    {(() => {
                      const now = new Date();
                      const expires = new Date(session.expires_at);
                      if (!session.is_active) {
                        return <span className="badge bg-secondary">종료됨</span>;
                      }
                      if (expires < now) {
                        return <span className="badge bg-warning">만료됨</span>;
                      }
                      return <span className="badge bg-success">활성</span>;
                    })()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="button"
                        className="btn fourbtn"
                        value="상세"
                        onClick={() => handleViewDetail(session, 'medical_bypass')}
                      />
                      {session.is_active && new Date(session.expires_at) > new Date() ? (
                        <input
                          type="button"
                          className="btn fourbtn btn-danger"
                          value="종료"
                          onClick={() => handleTerminateConfirm(session, 'medical_bypass')}
                        />
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // 정책 설정 폼 렌더링
  const renderPolicyForm = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      <div>
        <label className="form-label fw-bold">사용자 세션 만료 (일)</label>
        <input
          type="number"
          className="form-control"
          value={policies.session_user_timeout_days || 7}
          onChange={e => setPolicies(prev => ({ ...prev, session_user_timeout_days: e.target.value }))}
          min="1"
          max="30"
        />
      </div>
      <div>
        <label className="form-label fw-bold">관리자 세션 만료 (시간)</label>
        <input
          type="number"
          className="form-control"
          value={policies.session_admin_timeout_hours || 8}
          onChange={e => setPolicies(prev => ({ ...prev, session_admin_timeout_hours: e.target.value }))}
          min="1"
          max="24"
        />
      </div>
      <div>
        <label className="form-label fw-bold">최대 동시 세션 수</label>
        <input
          type="number"
          className="form-control"
          value={policies.session_max_concurrent || 1}
          onChange={e => setPolicies(prev => ({ ...prev, session_max_concurrent: e.target.value }))}
          min="1"
          max="10"
        />
      </div>
      <div>
        <label className="form-label fw-bold">비활성 타임아웃 (분)</label>
        <input
          type="number"
          className="form-control"
          value={policies.session_idle_timeout_minutes || 30}
          onChange={e => setPolicies(prev => ({ ...prev, session_idle_timeout_minutes: e.target.value }))}
          min="5"
          max="120"
        />
      </div>
      <div>
        <label className="form-label fw-bold">의료진 우회 최대 시간 (분)</label>
        <input
          type="number"
          className="form-control"
          value={policies.session_medical_bypass_max_minutes || 480}
          onChange={e => setPolicies(prev => ({ ...prev, session_medical_bypass_max_minutes: e.target.value }))}
          min="30"
          max="720"
        />
      </div>
      <div>
        <label className="form-label fw-bold">단일 세션 강제</label>
        <select
          className="form-select"
          value={policies.session_force_single || 'true'}
          onChange={e => setPolicies(prev => ({ ...prev, session_force_single: e.target.value }))}
        >
          <option value="true">예 (중복 로그인 불허)</option>
          <option value="false">아니오 (중복 로그인 허용)</option>
        </select>
      </div>
    </div>
  );

  return (
    <SectionContainer title="세션 관리" className="list">
      <Spinner visible={loading} />
      
      {/* 통계 카드 */}
      {renderStatisticsCards()}
      
      {/* 탭 */}
      <div className="mb-3">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              전체
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'user' ? 'active' : ''}`}
              onClick={() => setActiveTab('user')}
            >
              사용자 세션
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'medical_bypass' ? 'active' : ''}`}
              onClick={() => setActiveTab('medical_bypass')}
            >
              의료진 우회 세션
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'policies' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('policies');
                fetchPolicies();
              }}
            >
              세션 정책
            </button>
          </li>
        </ul>
      </div>
      
      {/* 필터 영역 */}
      {activeTab !== 'policies' && (
        <div className="search_area">
          <div className="acc_total">
            <h4>
              <b>{activeTab === 'medical_bypass' ? medicalPagination.total : userPagination.total}</b>개 검색되었습니다.
            </h4>
          </div>
          <div className="left">
            <div className="select_box">
              <select
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
              >
                <option value="active">활성</option>
                <option value="expired">만료/종료</option>
                <option value="all">전체</option>
              </select>
            </div>
            <div className="search_wrap insert-wrap">
              <div className="search">
                <div className="input">
                  <input
                    type="text"
                    value={filters.user_id}
                    onChange={e => handleFilterChange('user_id', e.target.value)}
                    onKeyUp={e => e.key === 'Enter' && fetchData()}
                    placeholder="사용자 ID 검색"
                    maxLength="50"
                  />
                </div>
                <i onClick={fetchData}>
                  <svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24.6582 21.6162L19.79 16.748C19.5703 16.5283 19.2725 16.4062 18.96 16.4062H18.1641C19.5117 14.6826 20.3125 12.5146 20.3125 10.1562C20.3125 4.5459 15.7666 0 10.1562 0C4.5459 0 0 4.5459 0 10.1562C0 15.7666 4.5459 20.3125 10.1562 20.3125C12.5146 20.3125 14.6826 19.5117 16.4062 18.1641V18.96C16.4062 19.2725 16.5283 19.5703 16.748 19.79L21.6162 24.6582C22.0752 25.1172 22.8174 25.1172 23.2715 24.6582L24.6533 23.2764C25.1123 22.8174 25.1123 22.0752 24.6582 21.6162ZM10.1562 16.4062C6.7041 16.4062 3.90625 13.6133 3.90625 10.1562C3.90625 6.7041 6.69922 3.90625 10.1562 3.90625C13.6084 3.90625 16.4062 6.69922 16.4062 10.1562C16.4062 13.6084 13.6133 16.4062 10.1562 16.4062Z" fill="#45A3FB"></path>
                  </svg>
                </i>
              </div>
            </div>
            <div className="insert-wrap">
              <button className="btn btn-md primary mr-1" onClick={handleResetFilters}>
                초기화
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 컨텐츠 영역 */}
      <div className="content_area">
        {activeTab === 'policies' ? (
          <div className="p-4">
            <h5 className="mb-4">세션 정책 설정</h5>
            {renderPolicyForm()}
            <div className="mt-4">
              <button className="btn btn-primary" onClick={handleSavePolicies}>
                정책 저장
              </button>
            </div>
          </div>
        ) : (
          <>
            {(activeTab === 'all' || activeTab === 'user') && (
              <>
                <h5 className="mb-3">사용자 세션</h5>
                {renderUserSessionsTable()}
              </>
            )}
            
            {(activeTab === 'all' || activeTab === 'medical_bypass') && (
              <>
                <h5 className="mb-3 mt-4">의료진 우회 세션</h5>
                {renderMedicalSessionsTable()}
              </>
            )}
          </>
        )}
      </div>
      
      {/* 페이지네이션 */}
      {activeTab !== 'policies' && userPagination.total_pages > 1 && (
        <div className="pagination_area mt-3">
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={userPagination.page === 1}
            onClick={() => setUserPagination(prev => ({ ...prev, page: 1 }))}
          >
            처음
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={userPagination.page === 1}
            onClick={() => setUserPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            이전
          </button>
          <span className="mx-3">
            {userPagination.page} / {userPagination.total_pages}
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={userPagination.page === userPagination.total_pages}
            onClick={() => setUserPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            다음
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={userPagination.page === userPagination.total_pages}
            onClick={() => setUserPagination(prev => ({ ...prev, page: userPagination.total_pages }))}
          >
            마지막
          </button>
        </div>
      )}
      
      {/* 세션 상세 모달 */}
      <Modal
        title="세션 상세 정보"
        visible={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedSession(null);
        }}
        className="terminal-info mt-2"
        modalClassName="modal-lg"
      >
        {selectedSession && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label className="form-label fw-bold">세션 ID</label>
              <input type="text" className="form-control" value={selectedSession.session_id} readOnly />
            </div>
            <div>
              <label className="form-label fw-bold">세션 유형</label>
              <input type="text" className="form-control" value={selectedSession.session_type === 'medical_bypass' ? '의료진 우회' : '사용자'} readOnly />
            </div>
            <div>
              <label className="form-label fw-bold">사용자 ID</label>
              <input type="text" className="form-control" value={selectedSession.user_id} readOnly />
            </div>
            <div>
              <label className="form-label fw-bold">사용자 이름</label>
              <input type="text" className="form-control" value={selectedSession.user_name} readOnly />
            </div>
            <div>
              <label className="form-label fw-bold">IP 주소</label>
              <input type="text" className="form-control" value={selectedSession.ip_address || '-'} readOnly />
            </div>
            <div>
              <label className="form-label fw-bold">생성 시간</label>
              <input type="text" className="form-control" value={formatDate(selectedSession.created_at)} readOnly />
            </div>
            <div>
              <label className="form-label fw-bold">마지막 활동</label>
              <input type="text" className="form-control" value={formatDate(selectedSession.last_active_at)} readOnly />
            </div>
            <div>
              <label className="form-label fw-bold">만료 시간</label>
              <input type="text" className="form-control" value={formatDate(selectedSession.expires_at)} readOnly />
            </div>
            {selectedSession.terminated_at && (
              <>
                <div>
                  <label className="form-label fw-bold">종료 시간</label>
                  <input type="text" className="form-control" value={formatDate(selectedSession.terminated_at)} readOnly />
                </div>
                <div>
                  <label className="form-label fw-bold">종료 사유</label>
                  <input type="text" className="form-control" value={selectedSession.termination_reason || '-'} readOnly />
                </div>
              </>
            )}
            {selectedSession.session_type === 'medical_bypass' && (
              <>
                <div>
                  <label className="form-label fw-bold">의료진 ID</label>
                  <input type="text" className="form-control" value={selectedSession.staff_id || '-'} readOnly />
                </div>
                <div>
                  <label className="form-label fw-bold">우회 레벨</label>
                  <input type="text" className="form-control" value={`레벨 ${selectedSession.bypass_level}`} readOnly />
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
      
      {/* 세션 종료 확인 모달 */}
      <Modal
        title="세션 종료 확인"
        visible={showTerminateModal}
        onClose={() => {
          setShowTerminateModal(false);
          setSelectedSession(null);
        }}
        onConfirm={handleTerminate}
        confirmLabel="종료"
        className="terminal-info mt-2"
      >
        <p>선택한 세션을 종료하시겠습니까?</p>
        {selectedSession && (
          <div className="mb-3">
            <p><strong>사용자:</strong> {selectedSession.user_id} ({selectedSession.user_name})</p>
            <p><strong>세션 유형:</strong> {selectedSession.session_type === 'medical_bypass' ? '의료진 우회' : '사용자'}</p>
          </div>
        )}
        <div>
          <label className="form-label">종료 사유 (선택)</label>
          <input
            type="text"
            className="form-control"
            value={terminateReason}
            onChange={e => setTerminateReason(e.target.value)}
            placeholder="종료 사유를 입력하세요"
          />
        </div>
      </Modal>
    </SectionContainer>
  );
};

export default SessionManagementPage;
